/* eslint-disable react-hooks/exhaustive-deps */
import {Communication} from "types/communication"
import cx from "classnames"
import styles from "./Message.module.css"
import {
    capitalize,
    checkPermission,
    checkStringMatchVideoProviders,
    copyTextToClipboard,
    displaySince,
    displaySinceWithDayInWeek,
    embedVideoLink,
    formatAmOrPM,
    handleErrorChat,
    replaceVideoLinksWithIframes,
    toastInfo,
    toastSuccess
} from "helpers"
import {useModel, useVisible} from "hooks"
import {UserAvatar} from "../UserAvatar"
import {useCallback, useContext, useEffect, useMemo, useState} from "react"
import {ReactComponent as MenuIcon} from "./assets/menu.svg"
import {ReactComponent as DateIcon} from "./assets/date.svg"
import {ReactComponent as TimeIcon} from "./assets/time.svg"
import {ReactComponent as SmsIcon} from "./assets/sms.svg"
import {ReactComponent as SmsNotSentIcon} from "./assets/smsNotSent.svg"
import {ReactComponent as EmailIcon} from "./assets/email.svg"
import {ReactComponent as PinIcon} from "./assets/pin.svg"
import {ReactComponent as LinkIcon} from "./assets/link.svg"
import {ReactComponent as RemindIcon} from "./assets/remind.svg"
import {ReactComponent as DoneIcon} from "./assets/done.svg"
import {ReactComponent as CommentIcon} from "./assets/comment.svg"
import {MessageOptionItem, MessageOptions} from "../MessageOptions"
import {ReplyMessage} from "../ReplyMessage"
import {EmojiPicker} from "../EmojiPicker"
import {chatServiceV2} from "services"
import {BaseLoading, Icon} from "components"
import {MediaViewer, PollV2, ReactionTooltip} from "./parts"
import {Dropdown, Menu, Tooltip} from "antd"
import moment from "moment"
import {CommunicationContext} from "context/CommunicationContext"
import PreviewPane from "components/HTMLPreviewEditor/PreviewPane"
import {getHtmlEmailMarkup} from "./htmlEmailMarkup"
import ChatWsV2 from "sections/NewCommunication/chatWsV2"
import {ReactComponent as ReplyEmailIcon} from "./assets/replyEmail.svg"
import {ReactComponent as ReplySmsIcon} from "./assets/replySms.svg"
import {LinkifyContent} from "components/ui"
import * as DOMPurify from "dompurify"
import {safeLinkifyHtml} from "utils/safeLinkifyHtml"
import {Link} from "react-router-dom"
import {YesNo} from "types/common"
import {Permissions} from "@edular/permissions"
import {CollegeSlug} from "types/clients"
import {SMS_STATUS} from "types/sms-logs"

export type MessageProps = {
    message: Communication.Message
    showTimestampSection?: boolean
    prevMessageTime?: string
    containerStyle?: string
    showOptions?: boolean
    compactMode?: boolean
    showScheduled?: boolean
    showFollowUp?: boolean
    followUpMessage?: Communication.FollowUpMessage
    navigate?: (_message: Communication.Message) => void
    onEditMessage?: (_message: Communication.Message) => void
    onReplyMessage?: (_message: Communication.Message) => void
    onDeleteMessage?: (messageId: number) => void
    onDoneFollowUp?: (followUpId: number) => void
    onUpdateFollowUp?: (_followUpMessage: Communication.FollowUpMessage, _time: number) => void
    onPublishMessage?: (messageId: number) => void
    highlightStudentMessage?: boolean
    onClickReplyMessage?: (_msg: Communication.ReplyMessage) => void
    roomMembers?: Communication.RoomUser[]
    isPost?: boolean
}

const MESSAGE_TYPES = {
    room: "New Thread",
    message: "Comment"
}

const ADDITIONAL_NOTES_ENGAGEMENT_SLUGS = new Set([
    CollegeSlug.COLLECTIV,
    CollegeSlug.COLLECTIVRIMA,
    CollegeSlug.ROBERTFIANCE
])

type AdditionalNotesEngagementMeta = {
    categoryLabel?: string
    items: {label: string; value: string}[]
}

function getAdditionalNotesEngagementMeta(
    slug: string,
    jsonData: Communication.Message["jsonData"]
): AdditionalNotesEngagementMeta | null {
    if (!ADDITIONAL_NOTES_ENGAGEMENT_SLUGS.has(slug as CollegeSlug)) {
        return null
    }
    const data = jsonData?.additionalNotesData
    if (!data?.engagement_type) {
        return null
    }
    const nonEmpty = (v: unknown) => v != null && String(v).trim() !== ""
    switch (data.engagement_type) {
        case "COMMUNICATION": {
            if (!nonEmpty(data.communicationLoggedFrom)) {
                return null
            }
            const fromCrm = String(data.communicationLoggedFrom) === "CRM"
            return {
                items: [{label: "Logged from", value: fromCrm ? "CRM" : "Chat"}]
            }
        }
        case "EMAIL": {
            if (!nonEmpty(data.emailDirection)) {
                return null
            }
            const displayDirection = String(data.emailDirection) === "EMAIL" ? "Inbound" : "Outbound"
            return {
                items: [{label: "Direction", value: displayDirection}]
            }
        }
        case "CALL": {
            const items: {label: string; value: string}[] = []
            if (nonEmpty(data.callDirection)) {
                items.push({label: "Direction", value: String(data.callDirection)})
            }
            if (nonEmpty(data.callDisposition)) {
                items.push({label: "Disposition", value: String(data.callDisposition)})
            }
            if (!items.length) {
                return null
            }
            return {items}
        }
        default:
            return null
    }
}

type SmsDeliveryUiState = "all" | "partial" | "none" | "notSent" | "unknown" | "queued"

const SMS_NOT_SENT_AFTER_MS = 5 * 60 * 1000

// Only real tags (`<p>`, `</div>`, `<a href="">`); text like `<9/28/2026>` or `<user@mail.com>` is not HTML
const HTML_TAG_REGEX = /<\/?[a-z][a-z0-9]*(\s[^<>]*)?\/?>/i

function isSmsStatusDelivered(status: string) {
    return String(status).trim().toLowerCase() === SMS_STATUS.DELIVERED
}

function isSmsStatusQueued(status: string) {
    return String(status).trim().toLowerCase() === SMS_STATUS.QUEUED
}

/** Expected SMS recipients for outbound `withSms` messages (matches chat-api targeting). */
function getExpectedSmsRecipientUserIds(
    activeRoom: Communication.Room,
    roomMembers: Communication.RoomUser[],
    senderUserId: number
): number[] {
    if (activeRoom.type === Communication.RoomTypes.Department) {
        const dept = activeRoom as Communication.DepartmentRoom
        const uid = dept.user?.userId ?? roomMembers.find((m) => m.isDepartmentChatUser)?.userId
        return uid != null ? [uid] : []
    }
    if (activeRoom.type === Communication.RoomTypes.DirectChat) {
        return roomMembers.filter((m) => m.userId !== senderUserId).map((m) => m.userId)
    }
    if (roomMembers.length) {
        return roomMembers.filter((m) => m.userId !== senderUserId).map((m) => m.userId)
    }
    return []
}

function getSmsDeliveryUiState(
    deliveries: Communication.MessageSmsDelivery[] | undefined,
    recipientUserIds: number[]
): SmsDeliveryUiState {
    if (!recipientUserIds.length) {
        return "unknown"
    }
    if (!deliveries?.length) {
        return "queued"
    }
    const expected = new Set(recipientUserIds)
    const deliveredRecipients = new Set<number>()
    const queuedRecipients = new Set<number>()
    for (const d of deliveries) {
        if (!expected.has(d.recipientUserId)) {
            continue
        }
        if (isSmsStatusDelivered(d.smsStatus)) {
            deliveredRecipients.add(d.recipientUserId)
        } else if (isSmsStatusQueued(d.smsStatus)) {
            queuedRecipients.add(d.recipientUserId)
        }
    }
    const deliveredCount = deliveredRecipients.size
    const queuedCount = queuedRecipients.size
    const n = recipientUserIds.length
    if (deliveredCount === n) {
        return "all"
    }
    if (queuedCount > 0 && deliveredCount < n) {
        return "queued"
    }
    if (queuedCount === 0 && deliveredCount === 0) {
        return "none"
    }
    return "partial"
}

const SMS_DELIVERY_TOOLTIPS: Record<SmsDeliveryUiState, string> = {
    all: "SMS delivered to all recipients",
    partial: "SMS partially delivered",
    none: "SMS not delivered",
    queued: "SMS in queue",
    notSent: "SMS not sent",
    unknown: "SMS"
}

const SMS_DELIVERY_STATE_CLASS: Record<SmsDeliveryUiState, string> = {
    all: styles.smsDeliveredAll,
    partial: styles.smsDeliveredPartial,
    none: styles.smsDeliveredNone,
    notSent: styles.smsNotSent,
    unknown: styles.smsDeliveredUnknown,
    queued: styles.smsDeliveredUnknown
}

const SMS_DELIVERY_ICON: Record<SmsDeliveryUiState, typeof SmsIcon> = {
    all: SmsIcon,
    partial: SmsIcon,
    none: SmsIcon,
    notSent: SmsNotSentIcon,
    unknown: SmsIcon,
    queued: SmsIcon
}

export function Message(props: MessageProps) {
    const {
        message: initialMessage,
        containerStyle,
        showOptions = false,
        compactMode = true,
        showScheduled = false,
        showFollowUp = false,
        showTimestampSection = false,
        prevMessageTime,
        followUpMessage,
        highlightStudentMessage,
        navigate,
        onEditMessage,
        onReplyMessage,
        onDeleteMessage,
        onDoneFollowUp,
        onUpdateFollowUp,
        onPublishMessage,
        onClickReplyMessage,
        roomMembers = [],
        isPost = false
    } = props
    const model = useModel()
    const userDateTimeFormat = model.getUserDateTimeFormat()
    const currentUserId = model.user.id
    const contextMenu = useVisible(false)
    const emojiPopup = useVisible(false)
    const mediaViewerPopup = useVisible(false)
    const [message, setMessage] = useState<Communication.Message>(initialMessage)
    const [isUpdating, setIsUpdating] = useState<boolean>(false)
    const {changeRoom, activeRoom, viewAs, foundMessageId, extendedInfoVisible, setShowUser} =
        useContext(CommunicationContext)
    const [selectedFile, setSelectedFile] = useState<Communication.AttachedFile | null>(null)
    const [smsQueuedTimedOut, setSmsQueuedTimedOut] = useState(false)

    useEffect(() => {
        const unSubscribeMessagePinStatus = ChatWsV2.registerOnMessagePinStatus(message.messageId, (data) => {
            handlerMessagePinStatus(data)
        })

        return () => {
            unSubscribeMessagePinStatus()
        }
    }, [message.messageId])

    const handlerMessagePinStatus = async (data: any) => {
        if (message.messageId === data.messageId) {
            setMessage((prev) => ({...prev, isPinned: data.isPinned}))
        }
    }

    useEffect(() => {
        setMessage(initialMessage)
    }, [initialMessage])

    const handleCopyTextClick = (text?: string) => {
        copyTextToClipboard(text || "").then(() => toastInfo(`Copied: ${text}`))
    }

    const isMine = model.user.id === message.author?.userId
    const showLinkChannel = showScheduled || (showFollowUp && followUpMessage && !!followUpMessage.room)
    const isCommentOrMessage = activeRoom.type !== Communication.RoomTypes.Channel || !message.isThread
    const isThread = activeRoom.type === Communication.RoomTypes.Channel && message.isThread
    const isDirectMessage = activeRoom.type === Communication.RoomTypes.DirectMessage

    const computedSmsDeliveryUiState = useMemo((): SmsDeliveryUiState | null => {
        if (!message.withSms) {
            return null
        }
        const recipientIds = getExpectedSmsRecipientUserIds(activeRoom, roomMembers, message.author?.userId ?? 0)
        const state = getSmsDeliveryUiState(message.smsDelivery, recipientIds)
        if (state === "queued" && message.createdAt) {
            const ageMs = Date.now() - new Date(message.createdAt).getTime()
            if (ageMs >= SMS_NOT_SENT_AFTER_MS) {
                return "notSent"
            }
        }
        return state
    }, [message.withSms, message.smsDelivery, message.author?.userId, message.createdAt, activeRoom, roomMembers])

    useEffect(() => {
        setSmsQueuedTimedOut(false)
        if (!message.withSms || computedSmsDeliveryUiState !== "queued" || !message.createdAt) {
            return
        }
        const remainingMs = SMS_NOT_SENT_AFTER_MS - (Date.now() - new Date(message.createdAt).getTime())
        if (remainingMs <= 0) {
            setSmsQueuedTimedOut(true)
            return
        }
        const timeoutId = setTimeout(() => setSmsQueuedTimedOut(true), remainingMs)
        return () => clearTimeout(timeoutId)
    }, [message.withSms, computedSmsDeliveryUiState, message.createdAt])

    const smsDeliveryUiState =
        smsQueuedTimedOut && computedSmsDeliveryUiState === "queued" ? "notSent" : computedSmsDeliveryUiState

    const showSmsQueuedLabel = smsDeliveryUiState === "queued"
    const SmsDeliveryIcon = smsDeliveryUiState && !showSmsQueuedLabel ? SMS_DELIVERY_ICON[smsDeliveryUiState] : null

    const messageOptionItems = useMemo(() => {
        const items: MessageOptionItem[] = []
        const isUneditable = message.withEmail || message.withSms || message.type === "poll"
        const isSupportRoom = activeRoom.type === Communication.RoomTypes.OpenedSupportHub
        const isChannelRoom = activeRoom.type === Communication.RoomTypes.Channel
        const isNotesRoom = activeRoom.type === Communication.RoomTypes.StudentTopic

        if (!!viewAs) {
            // Show delete option if view as other staff only
            if (viewAs.id === message.author?.userId && viewAs.hasPermissionToDelete === true) {
                items.push({
                    icon: "TRASH_LINE",
                    title: "Delete",
                    visible: true,
                    onClick: () => onDeleteMessage?.(message.messageId)
                })
            }
            return items
        }

        if (isCommentOrMessage && !isNotesRoom) {
            items.push({
                icon: "EMOJI_2",
                title: "Send reaction",
                visible: true,
                onClick: emojiPopup.open
            })
        }

        if (!isSupportRoom && !isNotesRoom) {
            items.push({
                icon: "FOLLOW_UP",
                title: "Follow up",
                visible: true,
                onClick: (data) => onFollowUpMessage(data)
            })
        }

        const canEdit =
            isMine ||
            (isChannelRoom &&
                checkPermission(
                    {
                        staff: [
                            isThread
                                ? Permissions.Staff.Chat.Channels.ThreadsOfAnotherPerson.Edit
                                : Permissions.Staff.Chat.Channels.CommentsOfAnotherPerson.Edit
                        ]
                    },
                    model
                ))
        if (canEdit && !!onEditMessage && !isUneditable) {
            items.push({
                icon: "EDIT_LINE",
                title: "Edit",
                visible: true,
                onClick: () => onEditMessage?.(message)
            })
        }

        if (!!onReplyMessage) {
            items.push({
                icon: "REPLY",
                title: "Reply to a message",
                visible: true,
                onClick: () => onReplyMessage?.(message)
            })
        }

        if (isCommentOrMessage && !isSupportRoom && !isNotesRoom) {
            items.push({
                icon: "PUSH_PIN",
                title: `${message.isPinned ? "Unpin" : "Pin"} message`,
                visible: true,
                onClick: () => onPinMessage()
            })
            if (!isMine) {
                items.push({
                    icon: "EYE_CROSSED_LINE",
                    title: "Mark unread",
                    visible: true,
                    onClick: () => onUnreadMessage()
                })
            }
        }

        if (isThread) {
            items.push({
                icon: "COPY",
                title: "Copy link",
                visible: true,
                onClick: () => {
                    handleCopyTextClick(
                        `${window.origin}/communication?roomId=${activeRoom.roomId}&threadId=${message.messageId}`
                    )
                }
            })
        }

        const canDelete =
            (isMine && model.chatOptions?.deleteOwnMessage === YesNo.Yes) ||
            (isChannelRoom &&
                checkPermission(
                    {
                        staff: [
                            isThread
                                ? Permissions.Staff.Chat.Channels.ThreadsOfAnotherPerson.Delete
                                : Permissions.Staff.Chat.Channels.CommentsOfAnotherPerson.Delete
                        ]
                    },
                    model
                ))
        if (canDelete && !isNotesRoom) {
            items.push({
                icon: "TRASH_LINE",
                title: "Delete",
                visible: true,
                onClick: () => onDeleteMessage?.(message.messageId)
            })
        }

        if (message.type === "poll" && message.poll) {
            const poll = message.poll!
            if (!poll.isClosed && poll.ownerId === currentUserId) {
                items.push({
                    icon: "CROSS_CIRCLE_FILL",
                    title: "Stop Poll",
                    visible: true,
                    onClick: () => onStopPoll()
                })
            }

            if (poll.hasVoted && poll.type !== "quiz" && !poll.isClosed) {
                items.push({
                    icon: "REPEAT",
                    title: "Retract Vote",
                    visible: true,
                    onClick: () => onRetractVote()
                })
            }
        }

        return items
    }, [message, viewAs])

    const shouldNavigate = useMemo(() => {
        return !!navigate
    }, [navigate])

    const scheduleThreadMenu = (
        <Menu className={styles.dropdownMenu}>
            {message.type !== "poll" && (
                <Menu.Item
                    icon={<Icon icon="EDIT_LINE" className={styles.menuIcon} />}
                    onClick={() => onEditMessage?.(message)}>
                    Edit thread
                </Menu.Item>
            )}
            <Menu.Item
                icon={<Icon icon="FOLLOW_UP" className={styles.menuIcon} />}
                onClick={() => onPublishMessage?.(message.messageId)}>
                Post now
            </Menu.Item>
            <Menu.Item
                icon={<Icon icon="TRASH_LINE" className={styles.menuIcon} />}
                onClick={() => onDeleteMessage?.(message.messageId)}>
                Delete thread
            </Menu.Item>
        </Menu>
    )

    const followUpRemindMenu = useMemo(() => {
        return (
            <Menu className={styles.dropdownMenu}>
                {Communication.FOLLOW_UP_TYPES.map((x) => (
                    <Menu.Item
                        key={x.label}
                        onClick={() =>
                            onUpdateFollowUp(followUpMessage, x.value)
                        }>{`Remind me in ${x.label}`}</Menu.Item>
                ))}
            </Menu>
        )
    }, [followUpMessage])

    const onAddReaction = async (e) => {
        onToggleReaction(e.native)
        emojiPopup.close()
    }

    const onToggleReaction = useCallback(async (reaction: string) => {
        if (viewAs) return
        try {
            setIsUpdating(true)
            await chatServiceV2.reactMessage({
                messageId: message.messageId,
                reaction
            })
            setMessage((prev) => {
                const _reactions = [...(prev.reactions || [])]
                const existReactionIdx = _reactions.findIndex((x) => x.reaction === reaction)
                if (existReactionIdx >= 0) {
                    const _existReaction = _reactions[existReactionIdx]
                    const _newCount = _existReaction.count + (_existReaction.me ? -1 : 1)
                    if (_newCount > 0) {
                        _reactions[existReactionIdx] = {
                            reaction,
                            count: _existReaction.count + (_existReaction.me ? -1 : 1),
                            me: !_existReaction.me
                        }
                    } else {
                        _reactions.splice(existReactionIdx, 1)
                    }
                } else {
                    _reactions.push({
                        reaction,
                        count: 1,
                        me: true
                    })
                }
                return {...prev, reactions: _reactions}
            })
        } catch (err) {
            handleErrorChat(err)
        } finally {
            setIsUpdating(false)
        }
    }, [])

    const onPinMessage = async () => {
        try {
            setIsUpdating(true)
            await chatServiceV2.pinMessage({
                messageId: message.messageId
            })
            const isPinned = !message.isPinned
            setMessage((prev) => ({...prev, isPinned}))
            toastSuccess(`You have ${isPinned ? "pinned" : "unpinned"} a message`)
        } catch (err) {
            handleErrorChat(err)
        } finally {
            setIsUpdating(false)
        }
    }

    const onUnreadMessage = async () => {
        try {
            setIsUpdating(true)
            await chatServiceV2.unreadMessage({
                messageId: message.messageId
            })
        } catch (err) {
            handleErrorChat(err)
        } finally {
            setIsUpdating(false)
        }
    }

    const onFollowUpMessage = async (time: number) => {
        try {
            setIsUpdating(true)
            await chatServiceV2.followUp({
                sourceId: message.messageId,
                sourceType: isThread ? "room" : "message",
                time
            })
            toastSuccess("Success")
        } catch (e) {
            handleErrorChat(e)
        } finally {
            setIsUpdating(false)
        }
    }

    const onRetractVote = async () => {
        try {
            if (message.type === "poll") {
                ChatWsV2.sendPacket({
                    command: Communication.SocketCommand.RetractPollVote,
                    payload: {
                        pollId: message.poll.pollId,
                        messageId: message.messageId
                    }
                })
            }
        } catch (e) {
            handleErrorChat(e)
        }
    }

    const onStopPoll = async () => {
        try {
            if (message.type === "poll") {
                await chatServiceV2.stopPoll({
                    pollId: message.poll.pollId,
                    messageId: message.messageId
                })
            }
        } catch (e) {
            handleErrorChat(e)
        }
    }

    const onDownloadFile = (file) => {
        const url = file.url
        const a = document.createElement("a")
        a.style.display = "none"
        a.href = url
        a.target = "_blank"
        a.download = file.filename
        a.click()
    }

    const goToChannel = (e) => {
        e.preventDefault()
        changeRoom(showScheduled ? message.room : followUpMessage.room)
    }

    const renderScheduledInfo = () => {
        if (!showScheduled) return null
        const userDateFormat = model.getUserDateFormat()
        const userTimeFormat = model.getUserTimeFormat()

        return (
            <div className={styles.scheduledInfo}>
                <div className={styles.scheduledInfoContainer}>
                    <span className={styles.scheduledTag}>Scheduled</span>
                    <div className={styles.scheduledValueTag}>
                        <div className={styles.scheduledValueWrap}>
                            <DateIcon className={styles.scheduledIcon} />
                            <span className={styles.scheduledValue}>
                                {moment(message.scheduledAt).format(userDateFormat)}
                            </span>
                        </div>
                        <div className={styles.scheduledValueWrap}>
                            <TimeIcon className={styles.scheduledIcon} />
                            <span className={styles.scheduledValue}>
                                {moment(message.scheduledAt).format(userTimeFormat)}
                            </span>
                        </div>
                    </div>
                </div>
                {!viewAs && (
                    <div onClick={(e) => e.stopPropagation()}>
                        <Dropdown overlay={scheduleThreadMenu} trigger={["hover"]} className={styles.iconWrap}>
                            <MenuIcon className={styles.icon} />
                        </Dropdown>
                    </div>
                )}
            </div>
        )
    }

    const renderInfo = () => {
        if (isPost) return null

        if (compactMode) {
            return (
                <div className={styles.infoContainer}>
                    {renderScheduledInfo()}
                    <div className={styles.info}>
                        {showFollowUp && (
                            <span className={styles.tag}>{MESSAGE_TYPES[followUpMessage?.sourceType]}</span>
                        )}
                        {message.author && (
                            <>
                                <UserAvatar user={message.author} className={styles.avatar} />
                                <span className={styles.fullName}>{message.author.fullName}</span>
                            </>
                        )}
                        {showLinkChannel && (
                            <>
                                <div className={styles.dot} />
                                <div className={styles.channelWrap} onClick={goToChannel}>
                                    <span className={styles.channelName}>
                                        {showScheduled ? message.room.name : followUpMessage.room.name}
                                    </span>
                                    <LinkIcon className={styles.channelIcon} />
                                </div>
                            </>
                        )}
                        <div className={styles.dot} />
                        <span className={styles.timestamp}>
                            {showTimestampSection
                                ? formatAmOrPM(new Date(message.createdAt))
                                : displaySince(new Date(message.createdAt), userDateTimeFormat)}
                        </span>
                        {message.isThread && (
                            <>
                                <div className={styles.dot} />
                                <span className={styles.timestamp}>{`${message.totalComments} ${
                                    message.totalComments > 1 ? "comments" : "comment"
                                }`}</span>
                                {message.unread > 0 && <span className={styles.unreadCount}>{message.unread}</span>}
                            </>
                        )}
                        {message.withSms && smsDeliveryUiState != null && showSmsQueuedLabel && (
                            <Tooltip title="SMS queued">
                                <span className={styles.smsQueuedLabel}>Queued</span>
                            </Tooltip>
                        )}
                        {message.withSms && smsDeliveryUiState != null && SmsDeliveryIcon && (
                            <Tooltip title={SMS_DELIVERY_TOOLTIPS[smsDeliveryUiState]}>
                                <div className={cx(styles.attContainer, SMS_DELIVERY_STATE_CLASS[smsDeliveryUiState])}>
                                    <SmsDeliveryIcon className={styles.attIcon} />
                                </div>
                            </Tooltip>
                        )}
                        {message.withEmail && (
                            <div className={styles.attContainer}>
                                <EmailIcon className={styles.attIcon} />
                            </div>
                        )}
                        {message.jsonData?.replyFromEmail && (
                            <div className={styles.attContainer}>
                                <ReplyEmailIcon className={styles.attIcon} />
                            </div>
                        )}
                        {message.jsonData?.replyFromSms && (
                            <div className={styles.attContainer}>
                                <ReplySmsIcon className={styles.attIcon} />
                            </div>
                        )}
                        {message.author.userId === currentUserId && message.isRead && (
                            <Icon icon="MESSAGE_READ" className={styles.tagIcon} />
                        )}
                    </div>
                </div>
            )
        }

        return (
            <div className={styles.expandedInfo}>
                {message.author && <UserAvatar user={message.author} className={styles.expandedAvatar} />}
                <div className={styles.expandedAuthor}>
                    <span className={styles.fullName}>{message.author?.fullName}</span>
                    <span className={styles.timestamp}>
                        {displaySince(new Date(message.createdAt), userDateTimeFormat)}
                    </span>
                </div>
            </div>
        )
    }

    const renderPostInfo = () => {
        if (!isPost) return null

        return (
            <div className={cx(styles.infoContainer, styles.postInfoContainer)}>
                <div className={cx(styles.info, styles.postInfoWrap)}>
                    {message.author && (
                        <div className={styles.commentWrap}>
                            <UserAvatar user={message.author} className={styles.avatar} />
                            <span className={styles.fullName}>{message.author.fullName}</span>
                        </div>
                    )}
                    <div className={styles.postMetadataWrap}>
                        <div className={styles.commentWrap}>
                            <CommentIcon />
                            <span className={cx(styles.timestamp, {[styles.unreadPostCount]: message.unread > 0})}>
                                {message.totalComments}
                            </span>
                        </div>
                        <span className={styles.timestamp}>{moment(new Date(message.createdAt)).fromNow()}</span>
                    </div>
                </div>
            </div>
        )
    }

    const renderAttachments = () => {
        if (isPost) return null

        const {files} = message
        const messageFiles = (files || []).filter(({url}) => !!url)
        const mediaFiles = messageFiles.filter(({type}) =>
            [Communication.AttachedFileType.Image, Communication.AttachedFileType.Video].includes(type)
        )
        const otherFiles = messageFiles.filter(
            ({type}) => ![Communication.AttachedFileType.Image, Communication.AttachedFileType.Video].includes(type)
        )

        return (
            <>
                <div className={styles.filesWrap}>
                    {mediaFiles.map((file, index) => {
                        if (file.type === Communication.AttachedFileType.Image) {
                            return (
                                <div
                                    key={index}
                                    className={styles.imgInner}
                                    onClick={(e) => {
                                        setSelectedFile(file)
                                        e.stopPropagation()
                                        mediaViewerPopup.open()
                                    }}>
                                    <img className={styles.image} src={file.url} alt="" />
                                </div>
                            )
                        }

                        const embedLink: string | null = embedVideoLink(file.url)
                        return embedLink ? (
                            <div
                                key={index}
                                className={styles.content}
                                dangerouslySetInnerHTML={{
                                    __html: DOMPurify.sanitize(replaceVideoLinksWithIframes(embedLink), {
                                        ADD_TAGS: ["iframe"],
                                        ADD_ATTR: ["allow", "allowfullscreen", "frameborder", "scrolling"]
                                    })
                                }}
                            />
                        ) : (
                            <div
                                key={index}
                                className={styles.videoInner}
                                onClick={(e) => {
                                    setSelectedFile(file)
                                    e.stopPropagation()
                                    mediaViewerPopup.open()
                                }}>
                                <span className={styles.playVideoIconContainer}>
                                    <Icon icon="PLAY_VIDEO" className={styles.playVideoIcon} color="#FAFAFA" />
                                </span>
                                <div className={styles.videoOverlay} />
                                <video
                                    className={styles.image}
                                    src={file.url}
                                    autoPlay={false}
                                    controls={false}
                                    poster={file.poster}
                                />
                            </div>
                        )
                    })}
                </div>
                <div className={styles.filesWrap}>
                    {otherFiles.map((file, index) => {
                        return (
                            <div key={index} className={styles.fileInner}>
                                <Icon icon="FILE" color="#939393" />
                                <div>
                                    <p title={file.filename}>{file.filename}</p>
                                </div>
                                <span
                                    className={styles.downloadWrapper}
                                    onClick={(e) => {
                                        e.stopPropagation()
                                        onDownloadFile(file)
                                    }}>
                                    <Icon icon="DOWNLOAD" color="#939393" />
                                </span>
                            </div>
                        )
                    })}
                </div>
            </>
        )
    }

    const renderPostAttachment = () => {
        if (!isPost) return null
        const {files} = message
        if (files.length === 0) return <div className={styles.postImagePlaceholder} />

        const imageFiles = files.filter(({type}) => type === Communication.AttachedFileType.Image)
        if (imageFiles.length > 0) {
            const file = imageFiles[0]
            return (
                <div
                    className={styles.postImageInner}
                    onClick={(e) => {
                        setSelectedFile(file)
                        e.stopPropagation()
                        mediaViewerPopup.open()
                    }}>
                    <img className={styles.image} src={file.url} alt="" />
                </div>
            )
        }
    }

    const renderReactions = () => {
        if ((message.reactions || []).length === 0) return null

        return (
            <div className={styles.reactionsContainer}>
                {(message.reactions || []).map((x, index) => {
                    return (
                        <ReactionTooltip
                            key={index}
                            messageId={message.messageId}
                            reaction={x.reaction}
                            count={x.count}>
                            <div
                                className={cx(styles.reactionWrap, {[styles.myReaction]: x.me})}
                                onClick={() => onToggleReaction(x.reaction)}>
                                <span className={styles.reaction}>{x.reaction}</span>
                                <span className={styles.reactionCount}>{x.count}</span>
                            </div>
                        </ReactionTooltip>
                    )
                })}
            </div>
        )
    }

    const renderTimestampHeader = () => {
        if (showTimestampSection) {
            let showTimestamp: boolean = true
            if (prevMessageTime) {
                showTimestamp = moment(message.createdAt).isAfter(prevMessageTime, "day")
            }
            if (showTimestamp) {
                return (
                    <div className={styles.separator}>
                        {displaySinceWithDayInWeek(message.createdAt, model.getUserDateFormat())}
                    </div>
                )
            }
        }

        return null
    }

    const renderSystemMessage = () => {
        const {type: systemType} = message.jsonData
        switch (systemType) {
            case "roomNameChanged":
                return (
                    <span>
                        <b>{message.author.fullName}</b>
                        {` changed the chat name to `}
                        <b>{message.jsonData?.name}</b>
                    </span>
                )
            case "roomMemberAdded": {
                const names = (message.jsonData?.users || []).map((u) => `<b>${u.fullName}</b>`).join(", ")
                return (
                    <span>
                        <b>{message.author.fullName}</b>
                        {` added `}
                        <span dangerouslySetInnerHTML={{__html: names}} />
                        {` to the group.`}
                    </span>
                )
            }
            case "roomMemberRemoved": {
                const names = (message.jsonData?.users || []).map((u) => `<b>${u.fullName}</b>`).join(", ")
                return (
                    <span>
                        <b>{message.author.fullName}</b>
                        {` removed `}
                        <span dangerouslySetInnerHTML={{__html: names}} />
                        {` from the group.`}
                    </span>
                )
            }
            case "pinMessage":
                return (
                    <span>
                        <b>{message.author.fullName}</b>
                        {` pinned `}
                        <b>a message</b>
                    </span>
                )
            case "unpinMessage":
                return (
                    <span>
                        <b>{message.author.fullName}</b>
                        {` unpinned `}
                        <b>a message</b>
                    </span>
                )
            case "outOfOfficeReply":
                return (
                    <span className={styles.systemMessageOutOfOffice}>
                        <b>{message.author.fullName}</b>
                        {" is out of office "}
                        {message.jsonData?.outOfOfficeEndAt
                            ? ` - back on ${moment(message.jsonData?.outOfOfficeEndAt).format("llll")}`
                            : null}
                        <br />
                        {message.content}
                    </span>
                )
            case "afterHourReply":
                return (
                    <span className={styles.systemMessageOutOfOffice}>
                        <b>{message.author.fullName}</b>
                        {" is outside working hours"}
                        <br />
                        {message.content}
                    </span>
                )
            default:
                return <span>{message.content}</span>
        }
    }

    if (message.type === "system") {
        return (
            <div className={styles.messageContainer} id={`message-${message.messageId}`}>
                <div className={styles.systemMessageContainer}>{renderSystemMessage()}</div>
            </div>
        )
    }

    const renderContent = () => {
        if (
            message.jsonData?.email?.editorType === Communication.EditorType.Html ||
            message.jsonData?.source?.editorType === Communication.EditorType.Html
        ) {
            return (
                <>
                    {!!message.jsonData?.email?.subject && (
                        <div className={styles.title}>{message.jsonData.email.subject}</div>
                    )}
                    <div className={styles.content}>
                        <PreviewPane variant="inline" markdown={getHtmlEmailMarkup(message)} />
                    </div>
                </>
            )
        }

        if (message.type === "poll" && message.poll) {
            return (
                <>
                    <div className={styles.content}>
                        <LinkifyContent content={message.content} />
                    </div>
                    <PollV2 poll={message.poll} messageId={message.messageId} />
                </>
            )
        }

        /**
         * NOTE
         * New lib "linkify-string" was used to fix showing special characters, issue reported at
         * https://github.com/Hypercontext/linkifyjs/issues/338
         */

        const shouldSerialize =
            message.withEmail ||
            message.withSms ||
            message.jsonData?.replyFromEmail ||
            isDirectMessage ||
            message.jsonData?.serialize
                ? true
                : false

        const linkDecorator = (decoratedHref, decoratedText, key) => {
            const origin = window.location.origin
            if (decoratedText.startsWith(origin)) {
                let newValue = decoratedText
                if (decoratedText.includes("/tasks/detail?")) {
                    newValue = "Click here to view the task details"
                }
                if (decoratedText.includes("/tasks/submit?")) {
                    newValue = "Click here to open the task"
                }
                return (
                    <Link key={key} to={decoratedHref.replace(origin, "")}>
                        {newValue}
                    </Link>
                )
            }
            return (
                <a key={key} href={decoratedHref} target={"_blank"} rel="noopener noreferrer">
                    {decoratedText}
                </a>
            )
        }
        const split = (message.content || "").split(/(\[@[^:]+:[^\]]+\])/gi)
        const result = []
        for (let index = 0; index < split.length; index++) {
            if (index % 2 === 1) {
                const [, user, id] = split[index].match(/\[(@[^:]+):([^\]]+)\]/i)
                result.push(
                    <span
                        key={`mention-${index}`}
                        onClick={() => {
                            extendedInfoVisible.open()
                            const user = roomMembers.find((x) => x.userId === +id)
                            if (user) setShowUser(user)
                        }}
                        className={cx(styles.mentioned, {[styles.mentionedHighlight]: currentUserId === +id})}>
                        {user}
                    </span>
                )
            } else if (shouldSerialize || HTML_TAG_REGEX.test(split[index])) {
                // checkStringMatchVideoProviders(split[index])
                result.push(
                    <div
                        key={`serialize-${index}`}
                        className={styles.content}
                        dangerouslySetInnerHTML={{
                            __html: DOMPurify.sanitize(safeLinkifyHtml(split[index]), {
                                ADD_TAGS: ["iframe"],
                                ADD_ATTR: ["allow", "allowfullscreen", "frameborder", "scrolling"]
                            })
                        }}
                    />
                )
            } else {
                result.push(<LinkifyContent key={`link-${index}`} content={split[index]} />)
            }
        }

        return (
            <>
                <div className={cx(styles.content, {[styles.postContent]: isPost})}>{result}</div>
                {message.jsonData?.linkPreviews?.length && (
                    <div className={styles.previewWrap}>
                        {message.jsonData.linkPreviews.map((link, index) => {
                            const {url, siteName, description, image, title} = link
                            const showPreview = checkStringMatchVideoProviders(url)
                            return (
                                <div key={index} className={styles.previewContainer}>
                                    {siteName && <div className={styles.previewSiteName}>{siteName}</div>}
                                    {title && (
                                        <a className={styles.previewTitle} href={url} target="_blank" rel="noreferrer">
                                            {title}
                                        </a>
                                    )}
                                    {description && <div className={styles.previewDesc}>{description}</div>}
                                    {showPreview && (
                                        <div
                                            dangerouslySetInnerHTML={{
                                                __html: DOMPurify.sanitize(replaceVideoLinksWithIframes(url), {
                                                    ADD_TAGS: ["iframe"],
                                                    ADD_ATTR: ["allow", "allowfullscreen", "frameborder", "scrolling"]
                                                })
                                            }}
                                        />
                                    )}
                                    {!showPreview && image && (
                                        <a href={url} target="_blank" rel="noreferrer">
                                            <img src={image} alt="" />
                                        </a>
                                    )}
                                </div>
                            )
                        })}
                    </div>
                )}
                {message.editedAt && (
                    <Tooltip title={moment(message.editedAt).format(userDateTimeFormat)}>
                        <span className={styles.edited}>(edited)</span>
                    </Tooltip>
                )}
            </>
        )
    }

    const additionalNotesEngagementMeta = getAdditionalNotesEngagementMeta(model.slug, message.jsonData)

    return (
        <div className={styles.messageContainer} id={`message-${message.messageId}`}>
            {renderTimestampHeader()}
            <div
                className={cx(
                    styles.container,
                    {[styles.navigateWrap]: shouldNavigate, [styles.highlightWrap]: highlightStudentMessage},
                    containerStyle,
                    {[styles.animatePulse]: foundMessageId === message.messageId},
                    {[styles.postMessageContainer]: isPost}
                )}
                onClick={() => navigate?.(message)}
                onMouseEnter={contextMenu.open}
                onMouseLeave={contextMenu.close}>
                <div className={styles.heading}>
                    {renderInfo()}
                    <div className={styles.rightHeading}>
                        {message.isPinned && (
                            <div className={styles.attContainer}>
                                <PinIcon className={styles.attIcon} />
                            </div>
                        )}
                        {!message.deletedAt &&
                            showOptions &&
                            contextMenu.isVisible &&
                            messageOptionItems.length > 0 && (
                                <MessageOptions onClose={contextMenu.close} items={messageOptionItems} />
                            )}
                        {showFollowUp && (
                            <>
                                <div className={styles.followUpTag}>
                                    <span className={styles.followUpTagTitle}>Followup reminder</span>
                                    <RemindIcon className={styles.tagIcon} />
                                </div>
                                <div
                                    className={styles.followUpAction}
                                    onClick={() => onDoneFollowUp(followUpMessage?.followUpId)}>
                                    <DoneIcon className={styles.followUpIcon} />
                                </div>
                                <div className={styles.followUpAction}>
                                    <Dropdown overlay={followUpRemindMenu} trigger={["hover"]} placement="bottomRight">
                                        <RemindIcon className={styles.followUpIcon} />
                                    </Dropdown>
                                </div>
                            </>
                        )}
                    </div>
                </div>
                <div className={styles.mainWrap}>
                    <div className={styles.main}>
                        {message.replyTo && message.replyTo.messageId && (
                            <ReplyMessage
                                message={message.replyTo}
                                containerStyle={styles.replyContainer}
                                onClick={(e) => {
                                    e.stopPropagation()
                                    onClickReplyMessage?.(message.replyTo)
                                }}
                            />
                        )}
                        {message.title && (
                            <span className={cx(styles.title, {[styles.postTitle]: isPost})}>
                                <LinkifyContent content={message.title} />
                            </span>
                        )}
                        {renderContent()}
                        {additionalNotesEngagementMeta && (
                            <div className={styles.additionalNotesEngagementWrap}>
                                <div className={styles.additionalNotesEngagementText}>
                                    <span className={styles.additionalNotesEngagementStrong}>CRM</span>
                                    {additionalNotesEngagementMeta.categoryLabel &&
                                        `" · " ${additionalNotesEngagementMeta.categoryLabel} · `}
                                    {additionalNotesEngagementMeta.items.map((item) => (
                                        <span key={item.label}>
                                            {" · "}
                                            {item.label}: {capitalize(item.value)}
                                        </span>
                                    ))}
                                </div>
                            </div>
                        )}
                        {renderAttachments()}
                    </div>
                    {renderPostAttachment()}
                </div>
                {renderPostInfo()}
                {renderReactions()}
                {isUpdating && <BaseLoading isShow={isUpdating} />}
            </div>
            {selectedFile && (
                <MediaViewer
                    isShow={mediaViewerPopup.isVisible}
                    onClose={() => {
                        setSelectedFile(null)
                        mediaViewerPopup.close()
                    }}
                    file={selectedFile}
                />
            )}
            <EmojiPicker
                isShow={emojiPopup.isVisible}
                onClose={emojiPopup.close}
                onSelect={onAddReaction}
                className={cx(styles.emojiPicker)}
            />
        </div>
    )
}
